import { useState } from "react";
import autopitchLogo from "@/assets/autopitch-logo.png";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { LanguageSelector } from "@/components/LanguageSelector";
import { MagneticButton } from "@/components/motion/MagneticButton";
import { Container } from "@/components/layout/Container";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface LandingNavProps {
  onSignIn?: () => void;
  showBackButton?: boolean;
  showHowItWorks?: () => void;
  showLanguageSelector?: boolean;
}

export function LandingNav({ onSignIn, showBackButton, showHowItWorks, showLanguageSelector }: LandingNavProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-border/60 bg-background/60 backdrop-blur-2xl supports-[backdrop-filter]:bg-background/40">
      {/* Aurora wash */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            "linear-gradient(90deg, hsl(var(--primary) / 0.08), transparent 30%, transparent 70%, hsl(var(--primary-glow) / 0.08))",
        }}
      />
      <Container size="wide" className="relative flex items-center justify-between h-14 md:h-16">
        <button onClick={() => navigate("/auth")} className="flex items-center gap-2.5 group min-h-11">
          <img src={autopitchLogo} alt="Auto Pitch" className="h-10 w-10 md:h-9 md:w-9 object-contain" />
          <span className="text-base md:text-sm font-semibold text-foreground tracking-tight">AutoPitch</span>
          {/* Live ONLINE pill — small ambient signal */}
          <span className="hidden lg:inline-flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full border border-success/30 bg-success/5 text-[9px] font-mono uppercase tracking-widest text-success">
            <span
              aria-hidden
              className="h-1.5 w-1.5 rounded-full bg-success"
              style={{
                boxShadow: "0 0 6px hsl(var(--success) / 0.7)",
                animation: "breathe-soft 2.4s ease-in-out infinite",
              }}
            />
            Online
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-1 lg:gap-2">
          {showBackButton && (
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1.5 min-h-9" onClick={() => navigate("/auth")}>
              ← Back
            </Button>
          )}
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground min-h-9" onClick={() => navigate("/pricing")}>
            Pricing
          </Button>
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground min-h-9" onClick={() => navigate("/blog")}>
            Blog
          </Button>
          {showHowItWorks && (
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground min-h-9" onClick={showHowItWorks}>
              How It Works
            </Button>
          )}
          {showLanguageSelector && <LanguageSelector current="en" />}
          <MagneticButton strength={5}>
            <Button size="sm" className="text-xs glow-primary min-h-9" onClick={onSignIn || (() => navigate("/auth"))}>
              Sign In
            </Button>
          </MagneticButton>
        </div>

        {/* Mobile cluster */}
        <div className="flex md:hidden items-center gap-1">
          {showLanguageSelector && <LanguageSelector current="en" />}
          <Button size="sm" className="text-xs glow-primary h-10 px-3" onClick={onSignIn || (() => navigate("/auth"))}>
            Sign In
          </Button>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center h-11 w-11 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </Container>

      {/* Mobile drawer */}
      <div
        className={cn(
          "md:hidden overflow-hidden border-t border-border/40 bg-background/95 backdrop-blur-xl transition-[max-height,opacity] duration-300 ease-out",
          open ? "max-h-96 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        )}
      >
        <Container size="wide" className="py-3 flex flex-col gap-1">
          {showBackButton && (
            <button onClick={() => go("/auth")} className="flex items-center min-h-11 px-3 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40">
              ← Back
            </button>
          )}
          <button onClick={() => go("/pricing")} className="flex items-center min-h-11 px-3 rounded-md text-sm text-foreground hover:bg-muted/40">
            Pricing
          </button>
          <button onClick={() => go("/blog")} className="flex items-center min-h-11 px-3 rounded-md text-sm text-foreground hover:bg-muted/40">
            Blog
          </button>
          {showHowItWorks && (
            <button
              onClick={() => {
                setOpen(false);
                showHowItWorks();
              }}
              className="flex items-center min-h-11 px-3 rounded-md text-sm text-foreground hover:bg-muted/40"
            >
              How It Works
            </button>
          )}
        </Container>
      </div>
    </nav>
  );
}
